import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createCategoryIcon = (color) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
  iconSize: [24, 24], iconAnchor: [12, 12], popupAnchor: [0, -12]
});

const CATEGORY_COLORS = {
  'Education': '#3B82F6', 'Health Care': '#EF4444', 'Human Services': '#38BDF8',
  'Environment': '#0EA5E9', 'Arts, Culture & Humanities': '#8B5CF6',
  'Animal-Related': '#F59E0B', 'Youth Development': '#6366F1', 'default': '#64748B'
};

const INDUSTRIES = ['Technology', 'Healthcare', 'Finance', 'Retail', 'Manufacturing', 'Education', 'Energy', 'Other'];
const CAUSE_OPTIONS = [
  { id: 'education', label: 'Education', category: 'Education' },
  { id: 'healthcare', label: 'Healthcare', category: 'Health Care' },
  { id: 'environment', label: 'Environment', category: 'Environment' },
  { id: 'hunger', label: 'Hunger & Food', category: 'Food & Agriculture' },
  { id: 'youth', label: 'Youth & Children', category: 'Youth Development' },
  { id: 'animals', label: 'Animal Welfare', category: 'Animal-Related' },
  { id: 'arts', label: 'Arts & Culture', category: 'Arts, Culture & Humanities' },
  { id: 'community', label: 'Community', category: 'Human Services' },
];

const MapController = ({ charities }) => {
  const map = useMap();
  useEffect(() => {
    if (charities.length > 0) {
      const bounds = L.latLngBounds(charities.map(c => [c.latitude, c.longitude]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }
  }, [charities, map]);
  return null;
};

// Score Badge Component
const ScoreBadge = ({ score, label, color }) => (
  <div className="text-center">
    <div className={`w-14 h-14 rounded-full ${color} flex items-center justify-center mx-auto mb-1`}>
      <span className="text-white font-bold text-lg">{score}</span>
    </div>
    <span className="text-xs text-gray-600 font-medium">{label}</span>
  </div>
);

// Benefits List Component
const BenefitsList = ({ title, benefits }) => (
  <div className="bg-gray-50 rounded-xl p-4">
    <h4 className="font-semibold text-gray-900 mb-3">{title}</h4>
    <ul className="space-y-2">
      {benefits?.map((benefit, i) => (
        <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></span>
          {benefit}
        </li>
      ))}
    </ul>
  </div>
);

const BrowseCharities = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const location = useLocation();
  
  const [step, setStep] = useState(1);
  const [charities, setCharities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCharity, setSelectedCharity] = useState(null);
  const [charityDetails, setCharityDetails] = useState(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedCauses, setSelectedCauses] = useState([]);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [profileComplete, setProfileComplete] = useState(false);
  
  const [aiQuery, setAiQuery] = useState('');
  const [queryInterpretation, setQueryInterpretation] = useState('');
  
  const [campaignData, setCampaignData] = useState({ title: '', description: '', goal_amount: 10000 });
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  useEffect(() => {
    fetchBusinessProfile();
  }, []);

  const fetchBusinessProfile = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/business/profile`, { withCredentials: true });
      setBusinessProfile(response.data.profile);
      setProfileComplete(response.data.profile_complete);
      if (response.data.profile) {
        setSelectedIndustry(response.data.profile.industry || '');
        setSelectedCauses(response.data.profile.target_causes || []);
      }
      if (response.data.profile_complete) {
        setStep(3);
        fetchRecommendations();
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const handleCauseToggle = (causeId) => {
    setSelectedCauses(prev => 
      prev.includes(causeId) ? prev.filter(c => c !== causeId) : [...prev, causeId]
    );
  };

  const saveProfileAndGetRecommendations = async () => {
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/business/quick-profile`, {
        industry: selectedIndustry,
        target_causes: selectedCauses,
        csr_goals: selectedCauses.map(c => CAUSE_OPTIONS.find(o => o.id === c)?.label).filter(Boolean)
      }, { withCredentials: true });
      
      setProfileComplete(true);
      setStep(3);
      await fetchRecommendations();
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/ai/recommend-charities`, {}, { withCredentials: true });
      setCharities(response.data.recommendations || []);
    } catch (err) {
      console.error('Error fetching recommendations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSmartSearch = async () => {
    if (!aiQuery.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/ai/search-charities`, { query: aiQuery }, { withCredentials: true });
      setCharities(response.data.organizations || []);
      setQueryInterpretation(response.data.query_interpretation || '');
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCharitySelect = async (charity) => {
    setSelectedCharity(charity);
    setStep(4);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/charities/${charity.ein}`, { withCredentials: true });
      setCharityDetails(response.data);
    } catch (err) {
      console.error('Error fetching details:', err);
    }
  };

  const handleCreateCampaign = async () => {
    if (!selectedCharity) return;
    setCreatingCampaign(true);
    try {
      // Determine impact metric based on charity category
      const impactMetrics = {
        'Education': { metric: 'students supported', rate: 0.1 },
        'Health Care': { metric: 'patients helped', rate: 0.05 },
        'Human Services': { metric: 'people assisted', rate: 0.08 },
        'Environment': { metric: 'trees planted', rate: 0.5 },
        'Animal-Related': { metric: 'animals saved', rate: 0.15 },
        'Arts, Culture & Humanities': { metric: 'programs funded', rate: 0.02 },
        'default': { metric: 'lives impacted', rate: 0.1 }
      };
      const categoryImpact = impactMetrics[selectedCharity.category] || impactMetrics.default;
      
      await axios.post(`${BACKEND_URL}/api/campaigns`, {
        charity_name: selectedCharity.name,
        charity_ein: selectedCharity.ein,
        title: campaignData.title || `Support ${selectedCharity.name}`,
        description: campaignData.description || `Partner with ${selectedCharity.name} to make an impact.`,
        goal_amount: parseFloat(campaignData.goal_amount) || 10000,
        impact_metric: categoryImpact.metric,
        impact_conversion_rate: categoryImpact.rate,
      }, { withCredentials: true });
      navigate('/dashboard', { state: { campaignCreated: true, charityName: selectedCharity.name } });
    } catch (err) {
      console.error('Error creating campaign:', err);
      alert('Failed to create campaign. Please try again.');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const getCategoryColor = (category) => CATEGORY_COLORS[category] || CATEGORY_COLORS.default;

  // Step 1: Select Industry (only shown if profile not complete)
  const renderStep1 = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Let's set up your profile first</h2>
        <p className="text-gray-600">This helps us find charities that align with your brand</p>
        <p className="text-sm text-blue-600 mt-2">
          (You can also set this up from your <button onClick={() => navigate('/dashboard')} className="underline">Dashboard</button>)
        </p>
      </div>
      
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select your industry</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {INDUSTRIES.map(industry => (
            <button
              key={industry}
              onClick={() => setSelectedIndustry(industry)}
              className={`p-4 rounded-xl border-2 text-center font-medium transition-all ${
                selectedIndustry === industry
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-blue-300 text-gray-700'
              }`}
            >
              {industry}
            </button>
          ))}
        </div>
      </div>
      
      <button
        onClick={() => setStep(2)}
        disabled={!selectedIndustry}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Continue
      </button>
    </div>
  );

  // Step 2: Select Causes
  const renderStep2 = () => (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">What causes matter to your business?</h2>
        <p className="text-gray-600">Select the areas where you want to make an impact</p>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {CAUSE_OPTIONS.map(cause => (
          <button
            key={cause.id}
            onClick={() => handleCauseToggle(cause.id)}
            className={`p-5 rounded-xl border-2 text-center transition-all ${
              selectedCauses.includes(cause.id)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <span className={`font-medium ${selectedCauses.includes(cause.id) ? 'text-blue-700' : 'text-gray-700'}`}>
              {cause.label}
            </span>
          </button>
        ))}
      </div>
      
      <div className="flex gap-4">
        <button onClick={() => setStep(1)} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-4 rounded-xl font-semibold transition-colors">
          Back
        </button>
        <button
          onClick={saveProfileAndGetRecommendations}
          disabled={selectedCauses.length === 0 || loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold disabled:opacity-50 transition-colors"
        >
          {loading ? 'Finding matches...' : 'Find Charity Matches'}
        </button>
      </div>
    </div>
  );

  // Filter states
  const [filters, setFilters] = useState({
    state: '',
    category: '',
    sortBy: 'esg_score'
  });
  const [showMap, setShowMap] = useState(true);

  const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];
  
  const CATEGORIES = ['Education', 'Health Care', 'Environment', 'Human Services', 'Arts, Culture & Humanities', 'Animal-Related', 'Youth Development'];

  // Apply filters to charities
  const filteredCharities = charities.filter(c => {
    if (filters.state && c.state !== filters.state) return false;
    if (filters.category && c.category !== filters.category) return false;
    return true;
  }).sort((a, b) => {
    if (filters.sortBy === 'esg_score') return (b.esg_score || 0) - (a.esg_score || 0);
    if (filters.sortBy === 'marketing_score') return (b.marketing_score || 0) - (a.marketing_score || 0);
    if (filters.sortBy === 'brand_score') return (b.brand_score || 0) - (a.brand_score || 0);
    return 0;
  });

  // Get charities with valid coordinates for map
  const mappableCharities = filteredCharities.filter(c => c.latitude && c.longitude);

  // Step 3: Browse Recommendations with Filters & Map
  const renderStep3 = () => (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">AI-Recommended Charities</h2>
            <p className="text-gray-600">Based on your {businessProfile?.industry || 'company'} profile • {filteredCharities.length} matches found</p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowMap(!showMap)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showMap ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
            >
              {showMap ? 'Hide Map' : 'Show Map'}
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="flex gap-3">
          <input
            type="text"
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSmartSearch()}
            placeholder="Refine search: 'local education nonprofits' or 'high visibility environmental charities'"
            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={handleSmartSearch}
            disabled={loading}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl disabled:opacity-50 transition-colors"
          >
            {loading ? '...' : 'Search'}
          </button>
        </div>
        {queryInterpretation && (
          <p className="mt-2 text-sm text-blue-600">{queryInterpretation}</p>
        )}
      </div>

      <div className="flex gap-6">
        {/* Filters Sidebar */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-2xl shadow-lg p-5 sticky top-24">
            <h3 className="font-bold text-gray-900 mb-4">Filters</h3>
            
            {/* Location Filter */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <select
                value={filters.state}
                onChange={(e) => setFilters({...filters, state: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All States</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Sort By */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="esg_score">ESG Score (High to Low)</option>
                <option value="marketing_score">Marketing Value (High to Low)</option>
                <option value="brand_score">Brand Fit (High to Low)</option>
              </select>
            </div>

            {/* Clear Filters */}
            {(filters.state || filters.category) && (
              <button
                onClick={() => setFilters({ state: '', category: '', sortBy: 'esg_score' })}
                className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Map */}
          {showMap && mappableCharities.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6" style={{ height: '300px' }}>
              <MapContainer
                center={[39.8283, -98.5795]}
                zoom={4}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap contributors'
                />
                <MapController charities={mappableCharities} />
                {mappableCharities.map((charity, idx) => (
                  <Marker
                    key={`${charity.ein}-${idx}`}
                    position={[charity.latitude, charity.longitude]}
                    icon={createCategoryIcon(getCategoryColor(charity.category))}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-bold">{charity.name}</p>
                        <p className="text-gray-600">{charity.city}, {charity.state}</p>
                        <p className="text-xs mt-1">ESG: {charity.esg_score || 'N/A'}</p>
                        <button
                          onClick={() => handleCharitySelect(charity)}
                          className="mt-2 text-blue-600 hover:underline text-xs font-medium"
                        >
                          View Details →
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}

          {/* Charity Cards */}
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Finding the best charity matches for your business...</p>
            </div>
          ) : filteredCharities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {filteredCharities.map((charity, index) => (
                <div
                  key={`${charity.ein}-${index}`}
                  onClick={() => handleCharitySelect(charity)}
                  className="bg-white rounded-xl shadow-md p-5 cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-blue-300"
                >
                  {/* Category & Match */}
                  <div className="flex justify-between items-start mb-3">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full text-white" style={{ backgroundColor: getCategoryColor(charity.category) }}>
                      {charity.category}
                    </span>
                    {charity.match_reason && (
                      <span className="text-xs text-blue-600 font-medium">AI Recommended</span>
                    )}
                  </div>
                  
                  {/* Name & Location */}
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{charity.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{charity.city}, {charity.state}</p>
                  
                  {/* Score Badges */}
                  <div className="flex gap-2 mb-3">
                    <ScoreBadge score={charity.esg_score || 85} label="ESG" color="bg-sky-500" />
                    <ScoreBadge score={charity.marketing_score || 78} label="Marketing" color="bg-blue-500" />
                    <ScoreBadge score={charity.brand_score || 82} label="Brand" color="bg-purple-500" />
                  </div>
                  
                  {/* Quick Benefits */}
                  {charity.esg_benefits?.length > 0 && (
                    <p className="text-xs text-gray-600 mb-3 line-clamp-1">
                      {charity.esg_benefits[0]}
                    </p>
                  )}
                  
                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold text-sm transition-colors">
                    View Details
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No charities match your filters</h3>
              <p className="text-gray-600 mb-4">Try adjusting your filters or search criteria</p>
              <button 
                onClick={() => setFilters({ state: '', category: '', sortBy: 'esg_score' })}
                className="text-blue-600 hover:underline font-medium"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Step 4: Charity Details with Value Proposition
  const renderStep4 = () => selectedCharity && (
    <div className="max-w-5xl mx-auto">
      <button onClick={() => { setSelectedCharity(null); setStep(3); }} className="mb-4 text-blue-600 hover:underline flex items-center gap-1">
        Back to results
      </button>
      
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8">
          <div className="flex justify-between items-start">
            <div>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium mb-3 inline-block">
                {selectedCharity.category}
              </span>
              <h1 className="text-3xl font-bold mb-2">{selectedCharity.name}</h1>
              <p className="text-blue-100">{selectedCharity.city}, {selectedCharity.state} | EIN: {selectedCharity.ein}</p>
            </div>
            <div className="text-right">
              {selectedCharity.income_amount > 0 && (
                <div>
                  <p className="text-sm text-blue-200">Annual Revenue</p>
                  <p className="text-2xl font-bold">{formatCurrency(selectedCharity.income_amount)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Scores */}
        <div className="p-8 border-b">
          <h3 className="text-lg font-bold text-gray-900 mb-6">Business Value Assessment</h3>
          <div className="grid grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold text-2xl">{selectedCharity.esg_score || 85}</span>
              </div>
              <h4 className="font-semibold text-gray-900">ESG Score</h4>
              <p className="text-sm text-gray-500">Environmental, Social & Governance</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold text-2xl">{selectedCharity.marketing_score || 78}</span>
              </div>
              <h4 className="font-semibold text-gray-900">Marketing Value</h4>
              <p className="text-sm text-gray-500">Visibility & engagement potential</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-purple-500 flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold text-2xl">{selectedCharity.brand_score || 82}</span>
              </div>
              <h4 className="font-semibold text-gray-900">Brand Alignment</h4>
              <p className="text-sm text-gray-500">Fit with your company values</p>
            </div>
          </div>
        </div>
        
        {/* Benefits Grid */}
        <div className="p-8 border-b">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Partnership Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BenefitsList 
              title="ESG Impact" 
              benefits={selectedCharity.esg_benefits || ['Supports sustainable development', 'UN SDG alignment', 'Measurable impact metrics']} 
            />
            <BenefitsList 
              title="Marketing Value" 
              benefits={selectedCharity.marketing_benefits || ['Brand visibility opportunity', 'Content creation potential', 'Social media engagement']} 
            />
            <BenefitsList 
              title="Brand Recognition" 
              benefits={selectedCharity.brand_benefits || ['Enhanced reputation', 'Stakeholder trust', 'Values-driven positioning']} 
            />
          </div>
        </div>
        
        {/* Financial Info */}
        {charityDetails && (
          <div className="p-8 border-b bg-gray-50">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Financial Transparency</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="text-sm text-gray-500 mb-1">Total Revenue</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(charityDetails.financial_data?.total_revenue)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="text-sm text-gray-500 mb-1">Total Assets</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(charityDetails.financial_data?.total_assets)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="text-sm text-gray-500 mb-1">Tax Filings</p>
                <p className="text-xl font-bold text-gray-900">{charityDetails.filings_count || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="text-sm text-gray-500 mb-1">Tax Period</p>
                <p className="text-xl font-bold text-gray-900">{charityDetails.financial_data?.tax_period || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* CTA */}
        <div className="p-8 bg-gradient-to-r from-gray-50 to-blue-50">
          <div className="flex flex-col md:flex-row gap-4">
            <button
              onClick={() => setShowCampaignModal(true)}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold text-lg transition-colors"
            >
              Start Campaign with This Charity
            </button>
            <a
              href={`https://projects.propublica.org/nonprofits/organizations/${selectedCharity.ein}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white border-2 border-gray-200 hover:border-blue-300 text-gray-700 rounded-xl font-semibold text-center transition-colors"
            >
              View Full Profile
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-900 font-medium">
              Back to Dashboard
            </button>
            {/* Progress Steps */}
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4].map(s => (
                <div key={s} className={`flex items-center ${s < 4 ? 'gap-2' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {s}
                  </div>
                  {s < 4 && <div className={`w-8 h-1 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`}></div>}
                </div>
              ))}
            </div>
            <div className="text-sm text-gray-500 font-medium">
              {step === 1 && 'Industry'}
              {step === 2 && 'Causes'}
              {step === 3 && 'Browse'}
              {step === 4 && 'Details'}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </main>

      {/* Campaign Modal */}
      {showCampaignModal && selectedCharity && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your Campaign</h2>
            <p className="text-gray-600 mb-6">Partner with {selectedCharity.name}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Campaign Title</label>
                <input
                  type="text"
                  value={campaignData.title}
                  onChange={(e) => setCampaignData({ ...campaignData, title: e.target.value })}
                  placeholder={`Support ${selectedCharity.name}`}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Fundraising Goal ($)</label>
                <input
                  type="number"
                  value={campaignData.goal_amount}
                  onChange={(e) => setCampaignData({ ...campaignData, goal_amount: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Description</label>
                <textarea
                  rows="3"
                  value={campaignData.description}
                  onChange={(e) => setCampaignData({ ...campaignData, description: e.target.value })}
                  placeholder="Tell your employees and stakeholders why this partnership matters..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button onClick={() => setShowCampaignModal(false)} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-xl font-semibold transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleCreateCampaign}
                  disabled={creatingCampaign}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold disabled:opacity-50 transition-colors"
                >
                  {creatingCampaign ? 'Creating...' : 'Launch Campaign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowseCharities;
